import { Component, Input, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Phone input — currently a simple tel input using the same logic as hc-input.
 * The country code selector is deferred until phone number formats are consolidated.
 */
@Component({
  selector: 'hc-input-phone',
  templateUrl: './input-phone.component.html',
  styleUrls: ['./input-phone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputPhoneComponent),
      multi: true
    }
  ]
})
export class InputPhoneComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() inputId = '';
  @Input() name = '';
  @Input() hasError: boolean | null | undefined = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() autocomplete = '';

  value = '';
  focused = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: string): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInput(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.onChange(this.value);
  }

  onFocus(): void { this.focused = true; }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  get containerClass(): string {
    const classes = ['hc-input'];
    if (this.focused) classes.push('hc-input--focused');
    if (this.hasError) classes.push('hc-input--error');
    if (this.disabled) classes.push('hc-input--disabled');
    if (this.readonly) classes.push('hc-input--readonly');
    if (this.value) classes.push('hc-input--filled');
    return classes.join(' ');
  }
}
